<template>
  <IonPage color="white">
    <MapModalHeader>
      <DashboardToolbar></DashboardToolbar>
    </MapModalHeader>
    <IonContent color="white" class="bg-white">
      <IonRefresher :mode="refresherMode" slot="fixed" @ion-refresh="IonRefresh($event)">
        <IonRefresherContent :mode="refresherMode"></IonRefresherContent>
      </IonRefresher>
      <div class="p-6 pt-0">
        <SectionTitle>visible on map</SectionTitle>
        <div class="text-gray-500 italic p-3" v-if="noVisibleProperties">No Nearby Properties</div>
        <PropertyRow v-for="p of map.visibleProperties" :key="p._id" :property="p" />
        <div class="text-center p-2" v-if="map.canShowMoreVisibleProperties">
          <BaseClearButton size="small" @click="map.showMoreVisibleProperties()">Show More</BaseClearButton>
        </div>

        <SectionTitle class="mt-6">Recently Viewed</SectionTitle>
        <PropertyRow v-for="p of recentlyViewedProperties" :property="p" />
        <div class="text-center p-2" v-if="canShowMoreRecentProperties">
          <BaseClearButton size="small" @click="showNumberOfRecentlyViewedProperties += 5">Show More</BaseClearButton>
        </div>

        <SectionTitle class="my-6">Property Stats</SectionTitle>
        <PieChart @center-clicked="onChartCenterClicked" :data="pieData" :show-percentage="true" />

        <div class="h-0.5 bg-gray-100 my-10"></div>
        <div class="text-center font-semibold text-gray-700">
          <div class="text-gray-500">You are</div>
          <div>Currently Servicing</div>
        </div>
        <table class="mx-auto my-8">
          <tr v-for="s of servicingStats">
            <td class="pr-5"><i class="text-gray-500 text-5xl" :class="s.icon"></i></td>
            <td class="py-3">
              <div class="text-sky-700 text-2xl font-medium">{{ s.value.toLocaleString('en-US') }}</div>
              <div class="uppercase font-bold text-gray-500 text-sm">{{ s.name }}</div>
            </td>
          </tr>
        </table>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup lang="ts">
import { useNewMap } from '@/stores/map/map.store';
import { IonContent, IonPage, IonRefresher, IonRefresherContent, onIonViewWillEnter, useIonRouter } from '@ionic/vue';
import PropertyRow from '@/views/property-list/PropertyRow.vue';
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import { useUser } from '@/stores/user.store';
import { computed, onMounted, ref, shallowRef } from 'vue';
import PieChart, { Datum } from '@/components/charts/PieChart.vue';
import { PropertyService } from '@/http-services/property.service';
import { usePropertyFilter } from '@/stores/property-filter.store';
import { PropertyFilterGatewayStatusIds, PropertyFilterGatewayTypeIds } from 'common';
import { useRefresherMode } from '@/composables/use-refresher-mode';
import MapModalHeader from '../MapModalHeader.vue';
import DashboardToolbar from '../toolbars/DashboardToolbar.vue';

const pieData = shallowRef<Datum[]>([]);
const servicingStats = shallowRef<{ icon: string; name: string; value: number }[]>([]);

const DEFAULT_NUMBER_RECENT_PROPERTIES = 5;
const MAX_NUMBER_RECENT_PROPERTIES = 10;

const { mode: refresherMode } = useRefresherMode();
const propertyService = new PropertyService();
const map = useNewMap();
const user = useUser();
const router = useIonRouter();
const filter = usePropertyFilter();
const showNumberOfRecentlyViewedProperties = ref(DEFAULT_NUMBER_RECENT_PROPERTIES);
const recentlyViewedProperties = computed(() => {
  return user.recentlyViewedProperties.slice(0, showNumberOfRecentlyViewedProperties.value);
});
const canShowMoreRecentProperties = computed(() => {
  return (
    user.recentlyViewedProperties.length > showNumberOfRecentlyViewedProperties.value &&
    showNumberOfRecentlyViewedProperties.value < MAX_NUMBER_RECENT_PROPERTIES
  );
});
const noVisibleProperties = computed(() => {
  return !map.visibleProperties.length;
});

async function IonRefresh(e: CustomEvent) {
  await loadStats();
  (e.target as any)?.complete();
}

async function loadStats() {
  const stats = await propertyService.userDashboardStats();
  pieData.value = [
    { name: 'Online', color: 'blue', value: stats.onlineGatewayCount },
    { name: 'Offline', color: 'red', value: stats.offlineGatewayCount },
    {
      name: 'No Gateway',
      color: 'gray',
      value: Math.max(stats.propertyCount - stats.onlineGatewayCount - stats.offlineGatewayCount, 0),
    },
  ];
  servicingStats.value = [
    { name: 'Properties', icon: 'icon-property', value: stats.propertyCount },
    { name: 'Units', icon: 'icon-home', value: stats.unitCount },
    { name: 'Devices', icon: 'icon-transceiver', value: stats.deviceCount },
  ];
}

async function onChartCenterClicked(d: Datum) {
  if (d?.name === 'Online') {
    filter.initForm({ gatewayStatus: PropertyFilterGatewayStatusIds.ONLINE });
    await router.push({ path: '/property-list' });
  } else if (d?.name === 'Offline') {
    filter.initForm({ gatewayStatus: PropertyFilterGatewayStatusIds.OFFLINE });
    await router.push({ path: '/property-list' });
  } else if (d?.name === 'No Gateway') {
    filter.initForm({ gatewayType: PropertyFilterGatewayTypeIds.NOT_PROGRAMMED });
    await router.push({ path: '/property-list' });
  }
}

onMounted(() => {
  loadStats();
});

onIonViewWillEnter(() => {
  map.resetNumberOfVisibleProperties();
});
</script>

<style scoped>
/* For some reason Android needs this  */
ion-refresher {
  z-index: 10;
}
</style>
